import { createTheme } from '@mui/material/styles';

/** Sage / walnut design tokens — keep in sync with styles.css :root */
export const tokens = {
  bg: '#F3F6F1',
  surface: '#FFFEFB',
  ink: '#2C241B',
  inkMuted: '#6B6258',
  accent: '#5B7F5E',
  accentHover: '#4A6A4D',
  brown: '#8B7355',
  border: '#D8DDD4',
  ok: '#5B7F5E',
  okBg: '#E7F0E7',
  warn: '#B8893A',
  warnBg: '#F7EFD9',
  danger: '#A65D4E',
  dangerBg: '#F6E8E4',
};

const theme = createTheme({
  typography: {
    fontFamily: "'Figtree', sans-serif",
    h1: { fontFamily: "'Fraunces', 'Figtree', serif", fontWeight: 650 },
    h2: { fontFamily: "'Fraunces', 'Figtree', serif", fontWeight: 650 },
    h3: { fontFamily: "'Fraunces', 'Figtree', serif", fontWeight: 650 },
    h4: { fontFamily: "'Fraunces', 'Figtree', serif", fontWeight: 650 },
    h5: { fontFamily: "'Fraunces', 'Figtree', serif", fontWeight: 650 },
    button: { textTransform: 'none', fontWeight: 600 },
  },
  palette: {
    mode: 'light',
    primary: {
      main: tokens.accent,
      dark: tokens.accentHover,
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: tokens.brown,
      contrastText: '#FFFFFF',
    },
    background: {
      default: tokens.bg,
      paper: tokens.surface,
    },
    text: {
      primary: tokens.ink,
      secondary: tokens.inkMuted,
    },
    success: { main: tokens.ok },
    warning: { main: tokens.warn },
    error: { main: tokens.danger },
    divider: tokens.border,
  },
  shape: { borderRadius: 10 },
  spacing: 8,
  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          boxShadow: 'none',
          borderRadius: 10,
          minHeight: 40,
          paddingLeft: 16,
          paddingRight: 16,
          gap: 8,
          '&:hover': { boxShadow: 'none' },
        },
        sizeSmall: {
          minHeight: 36,
          paddingLeft: 12,
          paddingRight: 12,
          fontSize: 13,
        },
        containedPrimary: {
          backgroundColor: tokens.accent,
          '&:hover': { backgroundColor: tokens.accentHover },
        },
        outlined: {
          borderColor: tokens.border,
          color: tokens.ink,
          '&:hover': {
            borderColor: tokens.brown,
            backgroundColor: 'rgba(243, 246, 241, 0.8)',
          },
        },
        text: {
          color: tokens.inkMuted,
          '&:hover': { backgroundColor: 'rgba(44, 36, 27, 0.05)', color: tokens.ink },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 14,
          border: `1px solid ${tokens.border}`,
          boxShadow: '0 16px 40px rgba(44, 36, 27, 0.14)',
          backgroundImage: 'none',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontFamily: "'Fraunces', 'Figtree', serif",
          fontWeight: 650,
          color: tokens.ink,
          paddingTop: 20,
          paddingBottom: 8,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          paddingTop: '12px !important',
          paddingBottom: 8,
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '12px 24px 20px',
          gap: 10,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          border: `1px solid ${tokens.border}`,
          boxShadow: '0 12px 28px rgba(44, 36, 27, 0.12)',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: 14,
          fontWeight: 500,
          borderRadius: 8,
          margin: '2px 6px',
          '&:hover': { backgroundColor: 'rgba(91, 127, 94, 0.10)' },
          '&.Mui-selected': {
            backgroundColor: 'rgba(91, 127, 94, 0.14)',
            '&:hover': { backgroundColor: 'rgba(91, 127, 94, 0.18)' },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600 },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 500, minHeight: 42 },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { backgroundColor: tokens.accent, height: 2 },
      },
    },
    MuiTextField: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: tokens.border },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: tokens.brown },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: tokens.accent },
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          overflow: 'hidden',
        },
        toolbar: {
          alignItems: 'center',
          minHeight: '44px !important',
          height: 44,
          paddingLeft: 8,
          paddingRight: 4,
        },
        selectLabel: {
          margin: 0,
          lineHeight: 1.25,
          fontSize: 13,
        },
        displayedRows: {
          margin: 0,
          lineHeight: 1.25,
          fontSize: 13,
        },
        select: {
          marginTop: 0,
          marginBottom: 0,
          fontSize: 13,
          fontWeight: 600,
        },
        actions: {
          display: 'flex',
          alignItems: 'center',
          marginLeft: 4,
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        footerContainer: {
          minHeight: '44px !important',
          maxHeight: 44,
          borderTop: `1px solid ${tokens.border}`,
          backgroundColor: tokens.bg,
        },
      },
    },
  },
});

export default theme;
